import { EventProperties } from '@calm/evexia-analytics';
import { createContext, ReactNode, useContext, useMemo } from 'react';

export const AnalyticsEventPropsContext = createContext<EventProperties>({});

/**
 * Returns the merged ambient analytics event props provided by ancestor
 * `AnalyticsEventPropsProvider` boundaries. `useTracking`'s `track`
 * function reads this and merges the result into every event so subtrees
 * can attach surface-level context (e.g. PackClass, RowIndex,
 * RowItemIndex) without each call site having to pass them explicitly.
 */
export const useAnalyticsEventProps = () =>
    useContext(AnalyticsEventPropsContext);

interface AnalyticsEventPropsProviderProps {
    children: ReactNode;
    /**
     * Props to merge into every analytics event fired from descendants.
     * Merged on top of ambient props from ancestor providers (descendant
     * props win on conflict with ancestor props, but lose to props passed
     * explicitly to `track()`).
     */
    props?: EventProperties;
    /**
     * When true, discard ambient props from ancestor providers within
     * this subtree. Use for subtrees where inherited props would be
     * misleading — e.g. a play button inside a home pack card whose
     * click event is tracked separately and shouldn't carry home-row
     * attribution.
     */
    clear?: boolean;
}

/**
 * Provides ambient analytics event props that `track()` will merge into
 * every event fired from descendants. Nestable; child props are merged
 * on top of ancestor props. Pass `clear` to drop ancestor props entirely
 * within the subtree.
 */
export const AnalyticsEventPropsProvider = ({
    children,
    props,
    clear = false,
}: AnalyticsEventPropsProviderProps) => {
    const parent = useContext(AnalyticsEventPropsContext);
    const value = useMemo(
        () => ({ ...(clear ? {} : parent), ...(props ?? {}) }),
        [parent, props, clear]
    );
    return (
        <AnalyticsEventPropsContext.Provider value={value}>
            {children}
        </AnalyticsEventPropsContext.Provider>
    );
};
